<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import equal from "deep-equal";
import { Menu } from "floating-vue";
import moment from "moment";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import Avatar from "~/components/dumb/Avatar.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import UserTimeTracking from "~/components/timeTracking/UserTimeTracking.vue";
import { colorsByTheme } from "~/constants/style";
import { FORM_USER_PSEUDO_USER } from "~/constants/user";
import { PlayIcon, PlusIcon, StopIcon, TimeTrackingFieldIcon } from "~/icons";
import { ButtonStyle, CommandId, DropdownMenuItemKind, EditorMode, Placement } from "~/shared/enums";
import type {
  PropertyAnyTimeTracking,
  PropertyValueForKind,
  Task,
  TimeTracking,
  TimeTrackingEntry,
  User,
} from "~/shared/types";
import { useDataStore, usePageStore, useUserStore } from "~/stores";
import { timeTrackingEntryComparator } from "~/utils/comparator";
import {
  getActiveTotalTimeElapsedText,
  getDurationText,
  getTimeTrackingDurationText,
  useTimeTracker,
} from "~/utils/time";
import { getUserDisplayName, splitCurrentUserItem } from "~/utils/user";

type Value = PropertyValueForKind<PropertyAnyTimeTracking>;

const props = defineProps<{
  property: PropertyAnyTimeTracking;
  tasks: Task[];
  editorMode: EditorMode;
  value?: Value;
}>();

const emit = defineEmits<{
  update: [value?: Value];
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);

// TODO this only works for one value
const timeTracking = computed(() => {
  if (isFormMode.value) {
    return props.value ?? [];
  }
  return getPropertyValueFromTask(props.property, props.tasks[0]);
});

const hasTimeTracking = computed(() => timeTracking.value.length > 0);

const defaultDistance = computed(() => (isFormMode.value ? 28 : 0));
const calcDistance = ref(defaultDistance.value);
const dropdownRef = ref<InstanceType<typeof GlobalPopper> | null>(null);
const buttonRef = computed(() => dropdownRef.value?.triggerRef);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!buttonRef.value) {
    return;
  }
  calcDistance.value -= buttonRef.value.getBoundingClientRect().height + 1;
};
useResizeObserver(buttonRef, recalculateDistance);
watch(buttonRef, recalculateDistance, { once: true });

const focusDiv = ref<HTMLDivElement | null>(null);
const onShow = () => {
  nextTick(() => {
    focusDiv.value?.focus();
  });
};

const activeTimeEntry = computed(() => timeTracking.value.find((entry) => !entry.finishedAt));
const { now, startTimer, stopTimer } = useTimeTracker(activeTimeEntry);

const activeTimeElapsedDuration = computed(() => {
  if (!activeTimeEntry.value) {
    return moment.duration(0);
  }
  return moment.duration(moment(now.value).diff(moment(activeTimeEntry.value.startedAt)));
});

const activeTimeElapsedText = computed(() =>
  activeTimeEntry.value
    ? getDurationText(activeTimeElapsedDuration.value)
    : getTimeTrackingDurationText(timeTracking.value)
);

const activeTotalTimeElapsedText = computed(() =>
  getActiveTotalTimeElapsedText(timeTracking.value, activeTimeElapsedDuration.value)
);

const sortEntries = (tt: TimeTracking) => tt.toSorted(timeTrackingEntryComparator);

const stopTimeTracking = () => {
  if (!activeTimeEntry.value) {
    return;
  }

  const updatedEntry = { ...activeTimeEntry.value, finishedAt: new Date().toISOString() };
  const updatedTimeTracking = sortEntries(
    timeTracking.value.map((entry) => (entry === activeTimeEntry.value ? updatedEntry : entry))
  );

  if (isFormMode.value) {
    emit("update", updatedTimeTracking);
    return;
  }

  dataStore.updateTasks(
    props.tasks.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(props.property, task, updatedTimeTracking),
    }))
  );
};

const startOrStopTimeTracking = () => {
  if (activeTimeEntry.value) {
    stopTimeTracking();
    return;
  }

  const updatedEntries = sortEntries([
    ...timeTracking.value,
    {
      userDuid: userStore.duid,
      startedAt: new Date().toISOString(),
      finishedAt: null,
    },
  ]);

  if (isFormMode.value) {
    emit("update", updatedEntries);
    return;
  }

  dataStore.updateTasks(
    props.tasks.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, updatedEntries) }))
  );
};

const usersAndTimeTracking = computed(() => {
  const userDuidToTimeTracking = new Map<string, TimeTracking>();
  timeTracking.value.forEach((entry: TimeTrackingEntry) => {
    const userEntries: TimeTracking = userDuidToTimeTracking.get(entry.userDuid) ?? [];
    userEntries.push(entry);
    userDuidToTimeTracking.set(entry.userDuid, userEntries);
  });

  const res: [User, TimeTracking][] = [];
  const currentUser = dataStore.getUserByDuid(userStore.duid);
  const currentUserTimeTracking = userDuidToTimeTracking.get(userStore.duid);

  if (currentUser && currentUserTimeTracking) {
    res.push([currentUser, currentUserTimeTracking]);
  }

  [...dataStore.getUserList({ excludeAgents: true }), FORM_USER_PSEUDO_USER].forEach((user) => {
    if (user.duid === userStore.duid) {
      return;
    }
    const tracking = userDuidToTimeTracking.get(user.duid);
    if (!tracking) {
      return;
    }

    res.push([user, tracking]);
  });
  return res;
});

const removeEntry = (entry: TimeTrackingEntry) => {
  if (isFormMode.value) {
    emit(
      "update",
      timeTracking.value.filter((e) => !equal(e, entry))
    );
    return;
  }

  dataStore.updateTasks(
    props.tasks.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(
        props.property,
        task,
        timeTracking.value.filter((e) => !equal(e, entry))
      ),
    }))
  );
};

const addEntryForUser = (userDuid: string) => {
  const updatedEntries = sortEntries([
    ...timeTracking.value,
    {
      userDuid,
      startedAt: moment().subtract(1, "hours").toISOString(),
      finishedAt: moment().toISOString(),
    },
  ]);

  if (isFormMode.value) {
    emit("update", updatedEntries);
    return;
  }

  dataStore.updateTasks(
    props.tasks.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, updatedEntries) }))
  );
};

const updateEntry = (current: TimeTrackingEntry, updated: TimeTrackingEntry) => {
  const updatedEntries = sortEntries(timeTracking.value.map((entry) => (equal(entry, current) ? updated : entry)));

  if (isFormMode.value) {
    emit("update", updatedEntries);
    return;
  }

  dataStore.updateTasks(
    props.tasks.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, updatedEntries) }))
  );
};

const addEntryDropdownSections = computed(() => {
  const users = dataStore.getUserList({ excludeAgents: true });
  const { currentUserItem, otherItems: usersWithoutCurrent } = splitCurrentUserItem(
    users,
    userStore.duid,
    (user) => user.duid,
    dataStore.getUserByDuid(userStore.duid)
  );

  const usersWithEntries = new Set(timeTracking.value.map((entry) => entry.userDuid));

  const subscriberDuids = new Set(props.tasks.flatMap((task) => task.subscriberDuids));

  const makeUserItem = (user: User) => ({
    title: getUserDisplayName(user),
    kind: DropdownMenuItemKind.BUTTON,
    icon: Avatar,
    iconArgs: {
      user,
      class: "size-5 mr-0.5",
    },
    onClick: () => addEntryForUser(user.duid),
  });

  const relevantOtherUsers = usersWithoutCurrent.filter(
    (user) => usersWithEntries.has(user.duid) || subscriberDuids.has(user.duid)
  );
  const allOtherUsers = usersWithoutCurrent.filter(
    (user) => !usersWithEntries.has(user.duid) && !subscriberDuids.has(user.duid)
  );

  const sections = [];

  const relevantUsers = currentUserItem ? [currentUserItem, ...relevantOtherUsers] : relevantOtherUsers;
  if (relevantUsers.length > 0) {
    sections.push({
      title: "Relevant",
      items: relevantUsers.map(makeUserItem),
    });
  }

  if (allOtherUsers.length > 0) {
    sections.push({
      title: "All",
      items: allOtherUsers.map(makeUserItem),
    });
  }

  return sections;
});

watch(
  () => activeTimeEntry.value,
  (newValue) => {
    if (!newValue) {
      return;
    }
    startTimer();
  },
  { immediate: true }
);

onUnmounted(() => {
  stopTimer();
});
</script>

<template>
  <GlobalPopper
    v-if="!isBoardMode || hasTimeTracking"
    ref="dropdownRef"
    :type="PopperType.DROPDOWN"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :disabled="isBoardMode"
    :triggers="isContextMenuMode ? ['hover', 'focus'] : undefined"
    :popper-triggers="isContextMenuMode ? ['hover', 'focus'] : undefined"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :distance="calcDistance"
    :skidding="isFormMode ? -1 : undefined"
    no-auto-focus
    dont-propagate-close
    :theme="`dropdown-${pageStore.theme}`"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    class="flex cursor-pointer"
    :class="{ 'w-full max-w-full': isListMode || isFormMode }"
    @apply-show="onShow">
    <template #default>
      <DropdownMenuItemContent
        v-if="isContextMenuMode"
        :icon="TimeTrackingFieldIcon"
        :title="`${activeTimeEntry ? 'Stop' : 'Start'} timer`"
        is-submenu />

      <Tooltip
        v-else
        :disabled="isFormMode"
        :command-id="!isBoardMode ? CommandId.START_TIMER : undefined"
        :text="isBoardMode ? getPropertyDisplayTitle(property) : undefined"
        :block="isListMode || isFormMode"
        :height-block="isListMode || isFormMode"
        :class="isTaskDetailMode && 'w-full'">
        <div
          ref="feedbackTooltipNode"
          class="group/time-tracking flex size-full max-w-full cursor-pointer items-center text-sm"
          :class="{
            'px-1.5': isListMode,
            'justify-start rounded hover:bg-lt': isTaskDetailMode,
            'justify-end': isListMode,
          }">
          <div
            class="flex items-center justify-center rounded-sm text-md select-none"
            :class="{
              'border border-oncolor': isChipMode || isBoardMode,
              'h-[26px] gap-1 py-0.5 text-sm hover:bg-opposite/10': isChipMode,
              'h-4': isBoardMode,
              'gap-2': !isChipMode,
              'h-5 text-xs': !isChipMode && !isTaskDetailMode && !isFormMode,
              'px-2 text-sm hover:bg-lt': isFormMode || isTaskDetailMode,
              'px-1': !isFormMode && !isTaskDetailMode,
            }">
            <TimeTrackingFieldIcon v-if="isChipMode && !hasTimeTracking" class="icon-sm" />
            <Button
              v-else-if="isTaskDetailMode"
              :btn-style="ButtonStyle.SECONDARY"
              is-contrast
              :icon="activeTimeEntry ? StopIcon : PlayIcon"
              :icon-args="{ class: 'text-primary-base' }"
              borderless
              :a11y-label="`${activeTimeEntry ? 'Stop' : 'Start'} timer`"
              class="-mx-1 cursor-pointer"
              @click.stop="startOrStopTimeTracking" />
            <span
              :class="[
                ((isTaskDetailMode && !hasTimeTracking) || (isFormMode && !hasTimeTracking)) && 'text-vlt',
                hasTimeTracking && 'tabular-nums',
              ]">
              {{
                hasTimeTracking
                  ? activeTimeElapsedText
                  : isChipMode
                    ? "Timer"
                    : isFormMode
                      ? `${activeTimeEntry ? "Stop" : "Start"} timer`
                      : isListMode
                        ? ""
                        : "None"
              }}
            </span>
          </div>
          <div
            v-if="isListMode && !hasTimeTracking"
            class="flex size-full items-center justify-center text-transparent group-hover/time-tracking:text-vlt">
            <TimeTrackingFieldIcon class="icon-xs" />
          </div>
        </div>
      </Tooltip>
    </template>

    <template #popper>
      <Menu no-auto-focus>
        <div
          class="flex w-96 max-w-96 flex-col divide-y divide-md overflow-hidden rounded-sm border border-md bg-lt focus-ring-none app-drag-none"
          @click.stop
          @keydown.stop>
          <div ref="focusDiv" tabindex="0" class="focus:outline-none" />
          <div class="flex items-center justify-between px-2 py-1">
            <div class="flex items-center gap-2">
              <Button
                :btn-style="ButtonStyle.SECONDARY"
                :a11y-label="`${activeTimeEntry ? 'Stop' : 'Start'} timer`"
                is-contrast
                :icon="activeTimeEntry ? StopIcon : PlayIcon"
                :icon-args="{ class: 'text-primary-base' }"
                borderless
                @click="startOrStopTimeTracking" />

              <div class="text-lt tabular-nums select-none">
                {{ activeTimeEntry ? activeTimeElapsedText : "0:00:00" }}
              </div>
            </div>

            <div class="pr-1 text-sm text-lt tabular-nums select-none">{{ activeTotalTimeElapsedText }}</div>
          </div>

          <div v-if="usersAndTimeTracking.length" class="flex w-full flex-col divide-y divide-md">
            <UserTimeTracking
              v-for="[user, tracking] in usersAndTimeTracking"
              :key="user.duid"
              :user="user"
              :time-tracking="tracking"
              @update="(current, updated) => updateEntry(current, updated)"
              @delete="removeEntry" />
          </div>

          <DropdownMenu
            :sections="addEntryDropdownSections"
            :placement="Placement.RIGHT_TOP"
            class="border-b-0 py-1"
            mobile-include-back>
            <DropdownMenuItemContent :icon="PlusIcon" title="Add entry" is-submenu class="text-lt hover:bg-md" />
          </DropdownMenu>
        </div>
      </Menu>
    </template>
  </GlobalPopper>
</template>

<style>
@reference "#frontend.css";
.v-popper--theme-dropdown .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-dropdown .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
